import { useState, useMemo, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { WEEKS, MEAL_KEYS, MEAL_META, MealKey, MealOption, MealSection, ClientDietPlan, Client, MeasurementRecord, DIET_TYPES, DietType, FoodLibraryItem, DietHistoryEntry } from "./dietitianData";
import { clientsApi, foodsApi } from "../../api";
import classes from "./ClientDetail.module.scss";

type Tab = "overview" | "diet" | "measurements" | "progress";

// ── Diet type metadata ────────────────────────────────────
const DIET_TYPE_META: Record<DietType, { emoji: string; desc: string }> = {
  "Fat Loss":         { emoji: "🔥", desc: "Caloric deficit to reduce body fat" },
  "Weight Gain":      { emoji: "📈", desc: "Caloric surplus for healthy weight gain" },
  "Hypertrophy":      { emoji: "💪", desc: "High protein for muscle building" },
  "Texture Modified": { emoji: "🥣", desc: "Adjusted textures for swallowing needs" },
  "Therapeutic":      { emoji: "💊", desc: "Medical nutrition therapy plan" },
};

// ── Diet config modal ─────────────────────────────────────
const DietConfigModal = ({
  onSave, onClose, initialType, initialWeeks,
}: {
  onSave: (dt: DietType, pw: 1|2|3|4) => Promise<void>;
  onClose: () => void;
  initialType?: DietType;
  initialWeeks?: 1|2|3|4;
}) => {
  const [dt, setDt] = useState<DietType | "">(initialType ?? "");
  const [pw, setPw] = useState<1|2|3|4>(initialWeeks ?? 1);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState("");

  const handleSave = async () => {
    if (!dt) return;
    setSaving(true);
    setSaveError("");
    try {
      await onSave(dt, pw);
    } catch {
      setSaveError("Failed to save. Please try again.");
      setSaving(false);
    }
  };

  return (
    <div className={classes.modal_overlay} onClick={onClose}>
      <div className={classes.diet_config_modal} onClick={e => e.stopPropagation()}>

        {/* Header */}
        <div className={classes.diet_config_header}>
          <div className={classes.diet_config_header_icon}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/>
              <rect x="9" y="3" width="6" height="4" rx="1"/>
              <line x1="9" y1="12" x2="15" y2="12"/><line x1="9" y1="16" x2="13" y2="16"/>
            </svg>
          </div>
          <div>
            <h3 className={classes.diet_config_title}>Configure Diet Plan</h3>
            <p className={classes.diet_config_subtitle}>Select a diet type and set the plan duration</p>
          </div>
          <button className={classes.modal_close} onClick={onClose}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
          </button>
        </div>

        <div className={classes.diet_config_body}>

          {/* Diet type section */}
          <div className={classes.diet_config_section}>
            <div className={classes.diet_config_section_label}>
              <span className={classes.diet_config_step}>1</span>
              Choose Diet Type
            </div>
            <div className={classes.diet_type_grid}>
              {DIET_TYPES.map(t => {
                const meta = DIET_TYPE_META[t];
                const active = dt === t;
                return (
                  <button
                    key={t}
                    type="button"
                    className={`${classes.diet_type_card} ${active ? classes.diet_type_card_active : ""}`}
                    onClick={() => setDt(t)}
                  >
                    <span className={classes.diet_type_emoji}>{meta.emoji}</span>
                    <span className={classes.diet_type_name}>{t}</span>
                    <span className={classes.diet_type_desc}>{meta.desc}</span>
                    {active && (
                      <span className={classes.diet_type_check}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12"/></svg>
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Duration section */}
          <div className={classes.diet_config_section}>
            <div className={classes.diet_config_section_label}>
              <span className={classes.diet_config_step}>2</span>
              Set Duration
            </div>
            <div className={classes.week_picker}>
              {([1,2,3,4] as const).map(n => (
                <button
                  key={n}
                  type="button"
                  className={`${classes.week_pick_btn} ${pw === n ? classes.week_pick_active : ""}`}
                  onClick={() => setPw(n)}
                >
                  <span className={classes.week_pick_num}>{n}</span>
                  <span className={classes.week_pick_label}>{n === 1 ? "week" : "weeks"}</span>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={classes.diet_config_footer}>
          {saveError && <span style={{ fontSize: 12, color: "#d9534f", marginRight: "auto" }}>{saveError}</span>}
          <button className={classes.cancel_btn} onClick={onClose} disabled={saving}>Cancel</button>
          <button
            className={classes.save_btn}
            disabled={!dt || saving}
            onClick={handleSave}
          >
            {saving ? "Saving…" : dt ? `Save — ${dt} · ${pw} ${pw === 1 ? "week" : "weeks"}` : "Select a diet type"}
          </button>
        </div>
      </div>
    </div>
  );
};

type FoodForm = {
  name: string; quantity: string; unit: string;
  cal: string; protein: string; fat: string; carb: string;
};

const EMPTY_FORM: FoodForm = { name: "", quantity: "", unit: "g", cal: "", protein: "", fat: "", carb: "" };

const ClientDetail = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  // ── Remote data state ─────────────────────────────────
  const [localClient,   setLocalClient]   = useState<Client | null>(null);
  const [measurements,  setMeasurements]  = useState<MeasurementRecord[]>([]);
  const [dietPlan,      setDietPlan]      = useState<ClientDietPlan>({});
  const [pageLoading,   setPageLoading]   = useState(true);
  const [saving,        setSaving]        = useState(false);

  const [tab, setTab]         = useState<Tab>("overview");
  const [selWeek, setSelWeek] = useState<typeof WEEKS[number]>(WEEKS[0]);
  const [unlocked, setUnlocked] = useState<string[]>(["Week 1"]);

  // Diet plan config state
  const [dietType,      setDietType]      = useState<DietType | "">("");
  const [planWeeks,     setPlanWeeks]     = useState<1|2|3|4>(1);
  const [dietSetup,     setDietSetup]     = useState(false);
  const [addMealOpen,   setAddMealOpen]   = useState(false);
  const [dietMenuOpen,  setDietMenuOpen]  = useState(false);
  const [completing,       setCompleting]       = useState(false);
  const [confirmComplete,  setConfirmComplete]  = useState(false);
  const [dietHistory,   setDietHistory]   = useState<DietHistoryEntry[]>([]);
  const [historyLoaded, setHistoryLoaded] = useState(false);
  const [expandedHist,  setExpandedHist]  = useState<string | null>(null);

  // Food item kebab menu
  const [foodMenu,       setFoodMenu]       = useState<{ sectionId: string; optId: number } | null>(null);
  const [foodMenuAnchor, setFoodMenuAnchor] = useState<{ top: number; right: number } | null>(null);

  // Food modal state
  const [foodModal,       setFoodModal]       = useState<{ sectionId: string; editId?: number } | null>(null);
  const [foodForm,        setFoodForm]        = useState<FoodForm>(EMPTY_FORM);
  const [foodSaving,      setFoodSaving]      = useState(false);
  const [foodError,       setFoodError]       = useState("");
  const [foodFromLibrary, setFoodFromLibrary] = useState(false);
  const [foodMobileTab,   setFoodMobileTab]   = useState<"browse" | "manual">("browse");
  const [baseMacros,      setBaseMacros]      = useState<{ qty: number; cal: number; protein: number; fat: number; carb: number } | null>(null);
  const [browseOpen,      setBrowseOpen]      = useState(true);
  const [manualOpen,      setManualOpen]      = useState(false);

  // Food library state
  const [foodLibrary,    setFoodLibrary]    = useState<FoodLibraryItem[]>([]);
  const [librarySearch,  setLibrarySearch]  = useState("");
  const [libraryLoading, setLibraryLoading] = useState(false);

  // Measurements edit state
  const [measEdit,    setMeasEdit]    = useState(false);
  const [editingMeas,   setEditingMeas]   = useState<MeasurementRecord | null>(null);
  const [measMenuOpen,  setMeasMenuOpen]  = useState<string | null>(null);
  const [measForm,    setMeasForm]    = useState({
    date: "", weight: "", height: "", bodyFatPct: "", musclePct: "", waterPct: "", idealWeight: "",
    waist: "", arm: "", wrist: "", hips: "", fatClipper: "", visceralFat: "",
  });

  // Notes state
  const [notesText,   setNotesText]   = useState("");
  const [notesSaving, setNotesSaving] = useState(false);
  const [notesSaved,  setNotesSaved]  = useState(false);
  const [notesError,  setNotesError]  = useState("");

  // Profile edit state
  const [profileEdit,  setProfileEdit]  = useState(false);
  const [profileError, setProfileError] = useState("");
  const [profileForm,  setProfileForm]  = useState({
    name: "", email: "", phone: "", age: "", gender: "F", height: "",
    goal: "", startDate: "", startWeight: "", currentWeight: "",
    bodyFatPct: "", musclePct: "", waterPct: "",
  });

  // ── Ideal weight auto-calculation ────────────────────
  const calcIdealWeight = (h: number, a: number, g: string) => {
    const base  = h - 100;
    const hAdj  = g === "F" ? (h - 150) / 2 : (h - 150) / 4;
    const aAdj  = (a - 20) / 4;
    return +(base - hAdj + aAdj).toFixed(1);
  };
  const idealWeightCalc = (() => {
    const h = Number(profileForm.height);
    const a = Number(profileForm.age);
    if (!h || !a) return null;
    return calcIdealWeight(h, a, profileForm.gender);
  })();

  // ── Date helpers ──────────────────────────────────────
  const todayISO = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
  };
  const isoToDisplay = (iso: string) => {
    const [y, m, day] = iso.split("-").map(Number);
    return new Date(y, m - 1, day).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  };
  const MONTH_IDX: Record<string, number> = {
    Jan:0,Feb:1,Mar:2,Apr:3,May:4,Jun:5,Jul:6,Aug:7,Sep:8,Oct:9,Nov:10,Dec:11,
  };
  const parseDisplayDate = (display: string): number => {
    const m = display.match(/^(\w+)\s+(\d+),?\s+(\d+)$/);
    if (!m) return 0;
    return new Date(+m[3], MONTH_IDX[m[1]] ?? 0, +m[2]).getTime();
  };
  const displayToISO = (display: string): string => {
    const m = display.match(/^(\w+)\s+(\d+),?\s+(\d+)$/);
    if (!m) return todayISO();
    const mon = String((MONTH_IDX[m[1]] ?? 0) + 1).padStart(2, "0");
    return `${m[3]}-${mon}-${m[2].padStart(2, "0")}`;
  };
  const sortByDate = (arr: MeasurementRecord[]) =>
    [...arr].sort((a, b) => parseDisplayDate(b.date) - parseDisplayDate(a.date));

  // ── Fetch all client data on mount ────────────────────
  const fetchAll = useCallback(async () => {
    if (!id) return;
    setPageLoading(true);
    const [clientRes, measRes, dietRes] = await Promise.allSettled([
      clientsApi.get(id),
      clientsApi.getMeasurements(id),
      clientsApi.getDietPlan(id),
    ]);
    if (clientRes.status === "fulfilled") {
      const c: Client = clientRes.value.client;
      setLocalClient(c);
      setUnlocked(c.unlockedWeeks ?? ["Week 1"]);
      setNotesText(c.notes ?? "");
      setDietType(c.dietType ?? "");
      setPlanWeeks(c.planWeeks ?? 1);
      setProfileForm({
        name:          c.name          ?? "",
        email:         c.email         ?? "",
        phone:         c.phone         ?? "",
        age:           c.age        != null ? String(c.age)           : "",
        gender:        c.gender        ?? "F",
        height:        c.height     != null ? String(c.height)        : "",
        goal:          c.goal          ?? "",
        startDate:     c.startDate     ?? "",
        startWeight:   c.startWeight   != null ? String(c.startWeight)   : "",
        currentWeight: c.currentWeight != null ? String(c.currentWeight) : "",
        bodyFatPct:    c.bodyFatPct    != null ? String(c.bodyFatPct)    : "",
        musclePct:     c.musclePct     != null ? String(c.musclePct)     : "",
        waterPct:      c.waterPct      != null ? String(c.waterPct)      : "",
      });
    } else {
      console.error("Failed to load client:", clientRes.reason);
    }
    if (measRes.status === "fulfilled") {
      setMeasurements(sortByDate(measRes.value.measurements ?? []));
    } else {
      console.error("Failed to load measurements:", measRes.reason);
    }
    if (dietRes.status === "fulfilled") {
      setDietPlan(dietRes.value.dietPlan ?? {});
    } else {
      console.error("Failed to load diet plan:", dietRes.reason);
    }
    setPageLoading(false);
  }, [id]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  useEffect(() => {
    if (!foodMenu) return;
    const close = () => { setFoodMenu(null); setFoodMenuAnchor(null); };
    document.addEventListener("click", close);
    return () => document.removeEventListener("click", close);
  }, [foodMenu]);

  useEffect(() => {
    if (tab !== "diet") return;
    if (foodLibrary.length === 0) {
      setLibraryLoading(true);
      foodsApi.list()
        .then(res => setFoodLibrary(res.foods ?? []))
        .catch(() => {})
        .finally(() => setLibraryLoading(false));
    }
    if (!historyLoaded && id) {
      clientsApi.getDietHistory(id)
        .then(res => { setDietHistory(res.history ?? []); setHistoryLoaded(true); })
        .catch(() => {});
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  // ── All hooks before any early return ──────────────────
  const sumMeal = (opts: MealOption[]) =>
    opts.reduce(
      (acc, o) => ({ cal: acc.cal + o.cal, protein: acc.protein + o.protein, fat: acc.fat + o.fat, carb: acc.carb + o.carb }),
      { cal: 0, protein: 0, fat: 0, carb: 0 }
    );

  const dayTotals = useMemo(() => {
    const sections: MealSection[] = dietPlan[selWeek]?.sections ?? [];
    return sections
      .reduce(
        (acc, sec) => {
          const t = sec.items.reduce((a, o) => ({ cal: a.cal + o.cal, protein: a.protein + o.protein, fat: a.fat + o.fat, carb: a.carb + o.carb }), { cal: 0, protein: 0, fat: 0, carb: 0 });
          return { cal: acc.cal + t.cal, protein: acc.protein + t.protein, fat: acc.fat + t.fat, carb: acc.carb + t.carb };
        },
        { cal: 0, protein: 0, fat: 0, carb: 0 }
      );
  }, [dietPlan, selWeek]);

  // ── Early return after all hooks ───────────────────────
  if (pageLoading) return (
    <div className={classes.loading_wrap}>
      <div className={classes.spinner} />
    </div>
  );

  if (!localClient) return (
    <div className={classes.not_found}>
      <p>Client not found.</p>
      <button onClick={() => navigate("/dietitian/clients")}>← Back</button>
    </div>
  );

  const openProfileEdit = () => {
    const c = localClient;
    setProfileForm({
      name:          c.name          ?? "",
      email:         c.email         ?? "",
      phone:         c.phone         ?? "",
      age:           c.age        != null ? String(c.age)          : "",
      gender:        c.gender        ?? "F",
      height:        c.height     != null ? String(c.height)       : "",
      goal:          c.goal          ?? "",
      startDate:     c.startDate     ?? "",
      startWeight:   c.startWeight   != null ? String(c.startWeight)   : "",
      currentWeight: c.currentWeight != null ? String(c.currentWeight) : "",
      bodyFatPct:    c.bodyFatPct    != null ? String(c.bodyFatPct)    : "",
      musclePct:     c.musclePct     != null ? String(c.musclePct)     : "",
      waterPct:      c.waterPct      != null ? String(c.waterPct)      : "",
    });
    setProfileError("");
    setProfileEdit(true);
  };

  const saveProfile = async () => {
    if (!id) return;
    if (!profileForm.height || !profileForm.musclePct || !profileForm.waterPct) {
      setProfileError("Height, Muscle %, and Water % are required to complete the profile.");
      return;
    }
    setProfileError("");
    setSaving(true);
    const updates = {
      name:          profileForm.name  || localClient.name,
      email:         profileForm.email || localClient.email,
      phone:         profileForm.phone         || null,
      age:           profileForm.age           ? Number(profileForm.age)           : null,
      gender:        profileForm.gender,
      height:        profileForm.height        ? Number(profileForm.height)        : null,
      goal:          profileForm.goal          || null,
      startDate:     localClient.startDate || new Date().toISOString().slice(0, 10),
      startWeight:   profileForm.startWeight   ? Number(profileForm.startWeight)   : null,
      currentWeight: profileForm.currentWeight ? Number(profileForm.currentWeight) : null,
      idealWeight:   idealWeightCalc,
      bodyFatPct:    profileForm.bodyFatPct    ? Number(profileForm.bodyFatPct)    : null,
      musclePct:     profileForm.musclePct     ? Number(profileForm.musclePct)     : null,
      waterPct:      profileForm.waterPct      ? Number(profileForm.waterPct)      : null,
      status:        (profileForm.height && profileForm.musclePct && profileForm.waterPct)
                       ? (localClient.status === "pending" ? "active" : localClient.status)
                       : localClient.status,
    };
    try {
      await clientsApi.update(id, updates);
      setLocalClient(prev => prev ? { ...prev, ...updates } as Client : prev);
      setProfileEdit(false);
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const EMPTY_MEAS = { date: todayISO(), weight: "", height: "", bodyFatPct: "", musclePct: "", waterPct: "", idealWeight: "", waist: "", arm: "", wrist: "", hips: "", fatClipper: "", visceralFat: "" };

  const requiredMeasFields = ["weight", "waist", "arm", "wrist", "hips", "fatClipper"] as const;

  const saveMeasurement = async () => {
    if (!id) return;
    const missing = requiredMeasFields.some(f => !measForm[f]);
    if (missing) return;
    setSaving(true);
    try {
      const dateStr = isoToDisplay(measForm.date || todayISO());

      if (editingMeas?.id) {
        // Edit existing
        const updates: Record<string, unknown> = {
          date:        dateStr,
          weight:      measForm.weight      ? Number(measForm.weight)      : null,
          height:      measForm.height      ? Number(measForm.height)      : null,
          bodyFatPct:  measForm.bodyFatPct  ? Number(measForm.bodyFatPct)  : null,
          musclePct:   measForm.musclePct   ? Number(measForm.musclePct)   : null,
          waterPct:    measForm.waterPct    ? Number(measForm.waterPct)    : null,
          idealWeight: measForm.idealWeight ? Number(measForm.idealWeight) : null,
          waist:       measForm.waist       ? Number(measForm.waist)       : null,
          arm:         measForm.arm         ? Number(measForm.arm)         : null,
          wrist:       measForm.wrist       ? Number(measForm.wrist)       : null,
          hips:        measForm.hips        ? Number(measForm.hips)        : null,
          fatClipper:  measForm.fatClipper  ? Number(measForm.fatClipper)  : null,
          visceralFat: measForm.visceralFat ? Number(measForm.visceralFat) : null,
        };
        await clientsApi.updateMeasurement(id, editingMeas.id, updates);
        const updatedArr = sortByDate(measurements.map(m =>
          m.id === editingMeas.id ? { ...m, ...updates, date: dateStr } as MeasurementRecord : m
        ));
        setMeasurements(updatedArr);
        const latest = updatedArr[0];
        const profileSync: Record<string, unknown> = {};
        if (latest?.weight)     profileSync.currentWeight = latest.weight;
        if (latest?.bodyFatPct) profileSync.bodyFatPct    = latest.bodyFatPct;
        if (latest?.musclePct)  profileSync.musclePct     = latest.musclePct;
        if (latest?.waterPct)   profileSync.waterPct      = latest.waterPct;
        if (Object.keys(profileSync).length) {
          await clientsApi.update(id, profileSync);
          setLocalClient(prev => prev ? { ...prev, ...profileSync } as Client : prev);
          setProfileForm(prev => ({
            ...prev,
            ...(profileSync.currentWeight != null ? { currentWeight: String(profileSync.currentWeight) } : {}),
            ...(profileSync.bodyFatPct    != null ? { bodyFatPct:    String(profileSync.bodyFatPct)    } : {}),
            ...(profileSync.musclePct     != null ? { musclePct:     String(profileSync.musclePct)     } : {}),
            ...(profileSync.waterPct      != null ? { waterPct:      String(profileSync.waterPct)      } : {}),
          }));
        }
      } else {
        // New record
        const record: MeasurementRecord = {
          date:        dateStr,
          weight:      measForm.weight      ? Number(measForm.weight)      : undefined,
          height:      measForm.height      ? Number(measForm.height)      : undefined,
          bodyFatPct:  measForm.bodyFatPct  ? Number(measForm.bodyFatPct)  : undefined,
          musclePct:   measForm.musclePct   ? Number(measForm.musclePct)   : undefined,
          waterPct:    measForm.waterPct    ? Number(measForm.waterPct)    : undefined,
          idealWeight: measForm.idealWeight ? Number(measForm.idealWeight) : undefined,
          waist:       measForm.waist       ? Number(measForm.waist)       : undefined,
          arm:         measForm.arm         ? Number(measForm.arm)         : undefined,
          wrist:       measForm.wrist       ? Number(measForm.wrist)       : undefined,
          hips:        measForm.hips        ? Number(measForm.hips)        : undefined,
          fatClipper:  measForm.fatClipper  ? Number(measForm.fatClipper)  : undefined,
          visceralFat: measForm.visceralFat ? Number(measForm.visceralFat) : undefined,
        };
        const res = await clientsApi.addMeasurement(id, record as Record<string, unknown>);
        const recordWithId = { ...record, id: res?.id ?? record.id };
        const updatedArr = sortByDate([recordWithId, ...measurements]);
        setMeasurements(updatedArr);
        const latest = updatedArr[0];
        const profileSync: Record<string, unknown> = {};
        if (latest?.weight)     profileSync.currentWeight = latest.weight;
        if (latest?.bodyFatPct) profileSync.bodyFatPct    = latest.bodyFatPct;
        if (latest?.musclePct)  profileSync.musclePct     = latest.musclePct;
        if (latest?.waterPct)   profileSync.waterPct      = latest.waterPct;
        if (Object.keys(profileSync).length) {
          await clientsApi.update(id, profileSync);
          setLocalClient(prev => prev ? { ...prev, ...profileSync } as Client : prev);
          setProfileForm(prev => ({
            ...prev,
            ...(profileSync.currentWeight != null ? { currentWeight: String(profileSync.currentWeight) } : {}),
            ...(profileSync.bodyFatPct    != null ? { bodyFatPct:    String(profileSync.bodyFatPct)    } : {}),
            ...(profileSync.musclePct     != null ? { musclePct:     String(profileSync.musclePct)     } : {}),
            ...(profileSync.waterPct      != null ? { waterPct:      String(profileSync.waterPct)      } : {}),
          }));
        }
      }
      setMeasEdit(false);
      setEditingMeas(null);
      setMeasForm(EMPTY_MEAS);
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const openEditMeas = (m: MeasurementRecord) => {
    setEditingMeas(m);
    setMeasForm({
      date:        m.date ? displayToISO(m.date) : todayISO(),
      weight:      m.weight      != null ? String(m.weight)      : "",
      height:      m.height      != null ? String(m.height)      : "",
      bodyFatPct:  m.bodyFatPct  != null ? String(m.bodyFatPct)  : "",
      musclePct:   m.musclePct   != null ? String(m.musclePct)   : "",
      waterPct:    m.waterPct    != null ? String(m.waterPct)    : "",
      idealWeight: m.idealWeight != null ? String(m.idealWeight) : "",
      waist:       m.waist       != null ? String(m.waist)       : "",
      arm:         m.arm         != null ? String(m.arm)         : "",
      wrist:       m.wrist       != null ? String(m.wrist)       : "",
      hips:        m.hips        != null ? String(m.hips)        : "",
      fatClipper:  m.fatClipper  != null ? String(m.fatClipper)  : "",
      visceralFat: m.visceralFat != null ? String(m.visceralFat) : "",
    });
    setMeasEdit(true);
  };

  const deleteMeasurement = async (m: MeasurementRecord) => {
    if (!id || !m.id) return;
    setMeasMenuOpen(null);
    try {
      await clientsApi.deleteMeasurement(id, m.id);
      setMeasurements(prev => prev.filter(r => r.id !== m.id));
    } catch (err) { console.error(err); }
  };

  const toggleWeekLock = async (week: string) => {
    if (!id) return;
    const next = unlocked.includes(week)
      ? unlocked.filter(w => w !== week)
      : [...unlocked, week];
    setUnlocked(next);
    try { await clientsApi.update(id, { unlockedWeeks: next }); }
    catch (err) { console.error(err); }
  };

  const saveNotes = async () => {
    if (!id) return;
    setNotesSaving(true);
    setNotesSaved(false);
    setNotesError("");
    try {
      await clientsApi.update(id, { notes: notesText });
      setLocalClient(prev => prev ? { ...prev, notes: notesText } : prev);
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 2500);
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Failed to save notes";
      setNotesError(msg);
      console.error("Save notes error:", err);
    } finally { setNotesSaving(false); }
  };

  const weekSections: MealSection[] = dietPlan[selWeek]?.sections ?? [];



  // Add a new meal section to the current week
  const addMealSection = async (type: MealKey) => {
    if (!id) return;
    setAddMealOpen(false);
    const newSection: MealSection = { id: `${Date.now()}`, type, items: [] };
    const newPlan = { ...dietPlan };
    const weekData: { sections: MealSection[] } = { sections: [...(newPlan[selWeek]?.sections ?? []), newSection] };
    newPlan[selWeek] = weekData;
    setDietPlan(newPlan);
    try { await clientsApi.saveDietPlanWeek(id, selWeek, weekData as Record<string, unknown>); }
    catch (err) { console.error(err); }
  };

  // Remove a whole meal section
  const deleteMealSection = async (sectionId: string) => {
    if (!id) return;
    const newPlan = { ...dietPlan };
    const weekData: { sections: MealSection[] } = { sections: (newPlan[selWeek]?.sections ?? []).filter(s => s.id !== sectionId) };
    newPlan[selWeek] = weekData;
    setDietPlan(newPlan);
    try { await clientsApi.saveDietPlanWeek(id, selWeek, weekData as Record<string, unknown>); }
    catch (err) { console.error(err); }
  };

  // Open modal to add food to a section
  const openAddFood = (sectionId: string) => {
    setFoodForm(EMPTY_FORM);
    setLibrarySearch("");
    setFoodError("");
    setFoodFromLibrary(false);
    setBaseMacros(null);
    setFoodMobileTab("browse");
    setBrowseOpen(true);
    setManualOpen(false);
    setFoodModal({ sectionId });
  };

  // Open modal to edit existing food in a section
  const openEditFood = (sectionId: string, opt: MealOption) => {
    const match = opt.quantity.trim().match(/^(\d*\.?\d+)\s*(.*)$/);
    const qtyNum  = match ? match[1] : opt.quantity;
    const qtyUnit = match ? (match[2].trim() || "g") : "g";
    setFoodForm({
      name: opt.name, quantity: qtyNum, unit: qtyUnit,
      cal: String(opt.cal), protein: String(opt.protein),
      fat: String(opt.fat), carb: String(opt.carb),
    });
    setFoodFromLibrary(false);
    setBaseMacros({ qty: parseFloat(qtyNum) || 1, cal: opt.cal, protein: opt.protein, fat: opt.fat, carb: opt.carb });
    setFoodError("");
    setFoodModal({ sectionId, editId: opt.id });
  };

  const saveFood = async () => {
    if (!foodModal || !foodForm.name.trim()) {
      setFoodError("Food name is required.");
      return;
    }
    if (!id) {
      setFoodError("Client ID missing — please reload the page.");
      return;
    }
    const { sectionId, editId } = foodModal;
    setFoodSaving(true);
    setFoodError("");

    const qtyStr = foodForm.quantity.trim()
      ? (foodForm.unit.trim() ? `${foodForm.quantity.trim()} ${foodForm.unit.trim()}` : foodForm.quantity.trim())
      : "";
    const entry: MealOption = {
      id:       editId ?? Date.now(),
      name:     foodForm.name.trim(),
      quantity: qtyStr,
      cal:      Number(foodForm.cal)     || 0,
      protein:  Number(foodForm.protein) || 0,
      fat:      Number(foodForm.fat)     || 0,
      carb:     Number(foodForm.carb)    || 0,
    };

    const newPlan = { ...dietPlan };
    const sections = (newPlan[selWeek]?.sections ?? []).map(sec => {
      if (sec.id !== sectionId) return sec;
      const list = [...sec.items];
      if (editId !== undefined) {
        const idx = list.findIndex(o => o.id === editId);
        if (idx >= 0) list[idx] = entry; else list.push(entry);
      } else {
        list.push(entry);
      }
      return { ...sec, items: list };
    });
    const weekData = { sections };
    newPlan[selWeek] = weekData;

    // Save to Firestore first — show error if it fails
    try {
      await clientsApi.saveDietPlanWeek(id, selWeek, weekData as Record<string, unknown>);
    } catch (err: unknown) {
      setFoodError(`Failed to save: ${err instanceof Error ? err.message : "server error"}`);
      setFoodSaving(false);
      return;
    }

    // Plan saved — update local state and close modal
    setDietPlan(newPlan);
    setFoodModal(null);
    setFoodSaving(false);
  };

  const deleteFood = async (sectionId: string, optId: number) => {
    if (!id) return;
    const newPlan = { ...dietPlan };
    const sections = (newPlan[selWeek]?.sections ?? []).map(sec =>
      sec.id !== sectionId ? sec : { ...sec, items: sec.items.filter(o => o.id !== optId) }
    );
    const weekData = { sections };
    newPlan[selWeek] = weekData;
    setDietPlan(newPlan);
    try { await clientsApi.saveDietPlanWeek(id, selWeek, weekData as Record<string, unknown>); }
    catch (err) { console.error(err); }
  };

  const ff = (k: keyof FoodForm, v: string) => setFoodForm(p => ({ ...p, [k]: v }));

  const handleQtyChange = (v: string) => {
    setFoodForm(p => {
      if (!baseMacros) return { ...p, quantity: v };
      const newQty = parseFloat(v) || 0;
      const ratio = newQty / baseMacros.qty;
      return {
        ...p,
        quantity: v,
        cal:     String(Math.round(baseMacros.cal     * ratio)),
        protein: String(Math.round(baseMacros.protein * ratio)),
        fat:     String(Math.round(baseMacros.fat     * ratio)),
        carb:    String(Math.round(baseMacros.carb    * ratio)),
      };
    });
  };

  const weightLost = localClient.startWeight && localClient.currentWeight
    ? +(localClient.startWeight - localClient.currentWeight).toFixed(1) : null;
  const storedIdeal = localClient.idealWeight
    ?? (localClient.height && localClient.age
        ? calcIdealWeight(localClient.height, localClient.age, localClient.gender ?? "F")
        : null);
  const toGoal = localClient.currentWeight && storedIdeal
    ? +(localClient.currentWeight - storedIdeal).toFixed(1) : null;

  const renderOverview = () => (
    <div className={classes.overview_grid}>
      {/* Profile card */}
      <div className={classes.info_card}>
        <h3 className={classes.card_title}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
          Profile
          <button className={classes.edit_profile_btn} onClick={openProfileEdit}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
            </svg>
            {localClient.status === "pending" ? "Complete Profile" : "Edit"}
          </button>
        </h3>
        <div className={classes.info_rows}>
          {[
            ["Age",    localClient.age    ? `${localClient.age} years`                             : "—"],
            ["Gender", localClient.gender ? (localClient.gender === "F" ? "Female" : "Male")       : "—"],
            ["Phone",  localClient.phone  ?? "—"],
            ["Email",  localClient.email],
            ["Goal",   localClient.goal   ?? "—"],
            ["Since",  localClient.startDate ? new Date(localClient.startDate).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" }) : "—"],
          ].map(([k, v]) => (
            <div key={k} className={classes.info_row}>
              <span className={classes.info_key}>{k}</span>
              <span className={`${classes.info_val} ${v === "—" ? classes.info_empty : ""}`}>{v}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Body stats */}
      <div className={classes.info_card}>
        <h3 className={classes.card_title}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 12h18M3 6h18M3 18h18"/></svg>
          Body Stats
        </h3>
        <div className={classes.body_stats}>
          <div className={classes.body_stat}>
            <span className={classes.bs_val}>{localClient.startWeight ?? "—"}</span>
            <span className={classes.bs_lbl}>Start (kg)</span>
          </div>
          <div className={classes.body_stat}>
            <span className={`${classes.bs_val} ${classes.bs_current}`}>{localClient.currentWeight ?? "—"}</span>
            <span className={classes.bs_lbl}>Current (kg)</span>
          </div>
          <div className={classes.body_stat}>
            <span className={classes.bs_val}>{storedIdeal ?? "—"}</span>
            <span className={classes.bs_lbl}>Ideal (kg)</span>
          </div>
          <div className={classes.body_stat}>
            <span className={classes.bs_val}>{localClient.bodyFatPct != null ? `${localClient.bodyFatPct}%` : "—"}</span>
            <span className={classes.bs_lbl}>Body Fat</span>
          </div>
          <div className={classes.body_stat}>
            <span className={classes.bs_val}>{localClient.musclePct != null ? `${localClient.musclePct}%` : "—"}</span>
            <span className={classes.bs_lbl}>Muscle</span>
          </div>
          <div className={classes.body_stat}>
            <span className={classes.bs_val}>{localClient.waterPct != null ? `${localClient.waterPct}%` : "—"}</span>
            <span className={classes.bs_lbl}>Water %</span>
          </div>
        </div>
        {weightLost !== null && toGoal !== null && localClient.startWeight && storedIdeal ? (
          <div className={classes.progress_row}>
            <div className={classes.progress_bar}>
              <div
                className={classes.progress_fill}
                style={{ width: `${Math.max(0, Math.min(100, Math.round(weightLost / (localClient.startWeight - storedIdeal) * 100)))}%` }}
              />
            </div>
            <span className={classes.progress_lbl}>
              {weightLost > 0 ? `${weightLost} kg lost` : `${Math.abs(weightLost)} kg gained`} · {toGoal > 0 ? `${toGoal} kg to goal` : "Goal reached 🎉"}
            </span>
          </div>
        ) : (
          <p className={classes.no_data}>No weight data yet</p>
        )}
      </div>

      {/* Week lock control */}
      <div className={classes.info_card}>
        <h3 className={classes.card_title}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>
          Week Access Control
        </h3>
        <p className={classes.card_sub}>Toggle which weeks the client can view in their app.</p>
        <div className={classes.week_locks}>
          {dietType
            ? WEEKS.slice(0, planWeeks).map(w => {
                const isUnlocked = unlocked.includes(w);
                return (
                  <div key={w} className={classes.week_lock_row}>
                    <div className={classes.week_lock_left}>
                      <span className={`${classes.lock_icon} ${isUnlocked ? classes.lock_open : classes.lock_closed}`}>
                        {isUnlocked ? "🔓" : "🔒"}
                      </span>
                      <span className={classes.week_lock_name}>{w}</span>
                      {w === "Week 1" && <span className={classes.always_tag}>Always open</span>}
                    </div>
                    <button
                      className={`${classes.toggle_btn} ${isUnlocked ? classes.toggle_on : classes.toggle_off}`}
                      onClick={() => w !== "Week 1" && toggleWeekLock(w)}
                      disabled={w === "Week 1"}
                    >
                      {isUnlocked ? "Unlocked" : "Locked"}
                    </button>
                  </div>
                );
              })
            : <p className={classes.card_sub} style={{ marginTop: 8 }}>Configure the diet plan first to set week access.</p>
          }
        </div>
      </div>

      {/* Next appointment */}
      <div className={classes.info_card}>
        <h3 className={classes.card_title}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/></svg>
          Next Appointment
        </h3>
        {localClient.nextApt ? (
          <div className={classes.apt_box}>
            <div className={classes.apt_date}>{localClient.nextApt}</div>
            <span className={classes.apt_type_badge}>{localClient.nextAptType}</span>
          </div>
        ) : (
          <p className={classes.no_apt}>No upcoming appointment</p>
        )}
      </div>

      {/* Notes card — spans full width */}
      <div className={`${classes.info_card} ${classes.notes_card}`}>
        <h3 className={classes.card_title}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
          Notes for Client
        </h3>
        <textarea
          className={classes.notes_textarea}
          placeholder="Write notes visible to the client in their dashboard…"
          value={notesText}
          onChange={e => setNotesText(e.target.value)}
          rows={4}
        />
        <div className={classes.notes_footer}>
          {notesSaved  && <span className={classes.notes_saved}>Saved ✓</span>}
          {notesError  && <span className={classes.notes_error}>{notesError}</span>}
          <button
            className={classes.notes_save_btn}
            onClick={saveNotes}
            disabled={notesSaving}
          >
            {notesSaving ? "Saving…" : "Save Notes"}
          </button>
        </div>
      </div>
    </div>
  );

  const saveDietConfig = async (dt: DietType, pw: 1|2|3|4) => {
    if (!id) throw new Error("Client ID missing");
    const newUnlocked = (["Week 1", "Week 2", "Week 3", "Week 4"] as const)
      .slice(0, pw) as string[];
    await clientsApi.update(id, { dietType: dt, planWeeks: pw, unlockedWeeks: newUnlocked });
    const fresh = await clientsApi.get(id);
    const c: Client = fresh.client;
    setLocalClient(c);
    setDietType(c.dietType ?? dt);
    setPlanWeeks(c.planWeeks ?? pw);
    setUnlocked(c.unlockedWeeks ?? newUnlocked);
    setDietSetup(false);
  };

  const completeDiet = () => {
    if (!id || completing) return;
    setConfirmComplete(true);
  };

  const doCompleteDiet = async () => {
    if (!id) return;
    setConfirmComplete(false);
    setCompleting(true);
    try {
      await clientsApi.completeDietPlan(id);
      const entry: DietHistoryEntry = {
        id: `local_${Date.now()}`,
        dietType: dietType as DietType,
        planWeeks,
        weeks: Object.fromEntries(
          Object.entries(dietPlan).map(([w, v]) => [w, v as { sections: MealSection[] }])
        ),
        completedAt: { _seconds: Math.floor(Date.now() / 1000) },
      };
      setDietHistory(prev => [entry, ...prev]);
      setDietPlan({});
      setDietType("");
      setPlanWeeks(1);
      setUnlocked(["Week 1"]);
    } catch (err) {
      console.error("Complete diet error:", err);
    } finally {
      setCompleting(false);
    }
  };

  const renderDietHistory = () => (
    <div className={classes.diet_history_section}>
      <div className={classes.diet_history_header}>
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/>
        </svg>
        <span>Diet History</span>
        {dietHistory.length > 0 && (
          <span className={classes.diet_history_count}>{dietHistory.length}</span>
        )}
      </div>
      {dietHistory.length === 0 ? (
        <p className={classes.diet_history_empty}>No completed diet plans yet.</p>
      ) : (
        <div className={classes.diet_history_list}>
          {dietHistory.map(entry => {
            const date = entry.completedAt
              ? new Date(entry.completedAt._seconds * 1000).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
              : "Unknown date";
            const isOpen = expandedHist === entry.id;
            const weekKeys = Object.keys(entry.weeks ?? {});
            return (
              <div key={entry.id} className={classes.diet_history_entry}>
                <button
                  className={classes.diet_history_entry_header}
                  onClick={() => setExpandedHist(isOpen ? null : entry.id)}
                >
                  <div className={classes.diet_history_entry_left}>
                    <span className={classes.diet_history_badge}>{entry.dietType}</span>
                    <span className={classes.diet_history_weeks}>{entry.planWeeks} week{entry.planWeeks > 1 ? "s" : ""}</span>
                  </div>
                  <div className={classes.diet_history_entry_right}>
                    <span className={classes.diet_history_date}>Completed {date}</span>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" style={{ transform: isOpen ? "rotate(180deg)" : "none", transition: "transform 200ms ease" }}>
                      <polyline points="6 9 12 15 18 9"/>
                    </svg>
                  </div>
                </button>
                {isOpen && (
                  <div className={classes.diet_history_body}>
                    {weekKeys.length === 0 ? (
                      <p className={classes.diet_history_empty}>No meal data recorded.</p>
                    ) : weekKeys.map(wk => {
                      const sections = entry.weeks[wk]?.sections ?? [];
                      if (sections.length === 0) return null;
                      return (
                        <div key={wk} className={classes.diet_history_week}>
                          <div className={classes.diet_history_week_title}>{wk}</div>
                          {sections.map(sec => (
                            <div key={sec.id} className={classes.diet_history_meal}>
                              <span className={classes.diet_history_meal_name}>
                                {MEAL_META[sec.type]?.emoji} {sec.type}
                              </span>
                              <div className={classes.diet_history_foods}>
                                {sec.items.map((item, i) => (
                                  <div key={i} className={classes.diet_history_food_row}>
                                    <span>{item.name}{item.quantity ? ` · ${item.quantity}` : ""}</span>
                                    <span className={classes.diet_history_food_meta}>{item.cal} kcal · P {item.protein}g · F {item.fat}g · C {item.carb}g</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          ))}
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderDiet = () => {
    const activeDietType  = dietType;
    const activePlanWeeks = planWeeks;
    const availableWeeks  = WEEKS.slice(0, activePlanWeeks) as string[];

    // If no diet configured yet — show setup prompt
    // dietType is "" when not configured (set unconditionally in fetchAll)
    if (!activeDietType) {
      return (
        <div className={classes.diet_section}>
          <div className={classes.diet_setup_prompt}>
            <div className={classes.diet_setup_icon}>
              <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"><path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2"/><rect x="9" y="3" width="6" height="4" rx="1"/><line x1="9" y1="12" x2="15" y2="12"/><line x1="9" y1="16" x2="13" y2="16"/></svg>
            </div>
            <h3 className={classes.diet_setup_title}>Set Up Diet Plan</h3>
            <p className={classes.diet_setup_sub}>Choose a diet type and duration before adding meals.</p>
            <button className={classes.diet_setup_btn} onClick={() => setDietSetup(true)}>
              Configure Diet Plan
            </button>
          </div>
          {dietSetup && <DietConfigModal onSave={saveDietConfig} onClose={() => setDietSetup(false)} />}
          {renderDietHistory()}
        </div>
      );
    }

    return (
      <div className={classes.diet_section}>
        {/* Diet plan header */}
        <div className={classes.diet_plan_header}>
          <div className={classes.diet_plan_meta}>
            <span className={classes.diet_type_badge}>{activeDietType}</span>
            <span className={classes.diet_weeks_badge}>{activePlanWeeks} week{activePlanWeeks > 1 ? "s" : ""}</span>
          </div>
          <div className={classes.diet_menu_wrap}>
            <button
              className={classes.diet_menu_btn}
              onClick={() => setDietMenuOpen(p => !p)}
              title="Actions"
            >
              <span /><span /><span />
            </button>
            {dietMenuOpen && (
              <>
                <div className={classes.diet_menu_backdrop} onClick={() => setDietMenuOpen(false)} />
                <div className={classes.diet_menu_dropdown}>
                  <button className={classes.diet_menu_item} onClick={() => { setDietMenuOpen(false); setDietSetup(true); }}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                    </svg>
                    Change Plan
                  </button>
                  <button className={`${classes.diet_menu_item} ${classes.diet_menu_item_green}`} onClick={() => { setDietMenuOpen(false); completeDiet(); }} disabled={completing}>
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="20 6 9 17 4 12"/>
                    </svg>
                    Mark as Complete
                  </button>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Week tabs — only show weeks up to planWeeks */}
        <div className={classes.week_tabs}>
          {availableWeeks.map(w => (
            <button
              key={w}
              className={`${classes.week_tab} ${selWeek === w ? classes.week_tab_active : ""}`}
              onClick={() => setSelWeek(w as typeof WEEKS[number])}
            >
              {w}
            </button>
          ))}
        </div>

        {/* Daily totals bar */}
        <div className={classes.macro_bar}>
          <span className={classes.macro_bar_label}>
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M3 12h18M3 6h18M3 18h18"/></svg>
            Daily Totals — {selWeek}
          </span>
          <div className={classes.macro_pills}>
            {[
              { label: "Cal",     val: `${dayTotals.cal} kcal` },
              { label: "Protein", val: `${dayTotals.protein}g` },
              { label: "Fat",     val: `${dayTotals.fat}g`     },
              { label: "Carbs",   val: `${dayTotals.carb}g`    },
            ].map(m => (
              <div key={m.label} className={classes.macro_pill}>
                <span className={classes.macro_pill_lbl}>{m.label}</span>
                <span className={classes.macro_pill_val}>{m.val}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Meal sections */}
        <div className={classes.meal_grid}>
          {weekSections.map(sec => {
            const meta = MEAL_META[sec.type];
            const totals = sumMeal(sec.items);
            return (
              <div key={sec.id} className={classes.meal_card}>
                <div className={classes.meal_card_header}>
                  <span className={classes.meal_emoji}>{meta.emoji}</span>
                  <span className={classes.meal_name}>{sec.type}</span>
                  <div className={classes.meal_header_right}>
                    <span className={classes.meal_total_tag}>{totals.cal} kcal</span>
                    <button className={classes.add_food_btn} onClick={() => openAddFood(sec.id)}>
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round">
                        <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
                      </svg>
                      Add Food
                    </button>
                    <button className={`${classes.food_edit_btn} ${classes.food_del_btn}`} title="Remove section" onClick={() => deleteMealSection(sec.id)}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                        <polyline points="3 6 5 6 21 6"/>
                        <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/>
                        <path d="M10 11v6M14 11v6"/>
                      </svg>
                    </button>
                  </div>
                </div>
                {sec.items.length === 0 && <div className={classes.no_foods}>No foods added yet</div>}
                <div className={classes.food_list}>
                  {sec.items.map((opt: MealOption) => (
                    <div key={opt.id} className={classes.food_item_wrap}>
                      {opt.name && (
                        <div className={classes.food_row_label}>
                          {opt.name}{opt.quantity ? <span className={classes.food_qty_inline}> · {opt.quantity}</span> : null}
                        </div>
                      )}
                      <div className={classes.food_row}>
                        <div className={classes.food_macros}>
                          <span className={classes.macro_chip}>{opt.cal} kcal</span>
                          <span className={classes.macro_chip}>P {opt.protein}g</span>
                          <span className={classes.macro_chip}>F {opt.fat}g</span>
                          <span className={classes.macro_chip}>C {opt.carb}g</span>
                        </div>
                        <button
                          className={classes.kebab_btn}
                          onClick={e => {
                            e.stopPropagation();
                            if (foodMenu?.sectionId === sec.id && foodMenu?.optId === opt.id) {
                              setFoodMenu(null); setFoodMenuAnchor(null); return;
                            }
                            const rect = (e.currentTarget as HTMLElement).getBoundingClientRect();
                            setFoodMenuAnchor({ top: rect.bottom + 6, right: window.innerWidth - rect.right });
                            setFoodMenu({ sectionId: sec.id, optId: opt.id });
                          }}
                        >
                          <span/><span/><span/>
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })}

          {/* Add meal section button */}
          <div className={classes.add_meal_section_wrap}>
            <button className={classes.add_meal_section_btn} onClick={() => setAddMealOpen(true)}>
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/>
              </svg>
              Add Meal
            </button>
            {addMealOpen && (
              <div className={classes.meal_type_picker} onClick={() => setAddMealOpen(false)}>
                <div className={classes.meal_type_picker_box} onClick={e => e.stopPropagation()}>
                  <p className={classes.meal_type_picker_title}>Choose meal type</p>
                  {MEAL_KEYS.map(mk => (
                    <button key={mk} className={classes.meal_type_opt} onClick={() => addMealSection(mk)}>
                      <span>{MEAL_META[mk].emoji}</span>
                      <span>{mk}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Diet config modal */}
        {dietSetup && <DietConfigModal onSave={saveDietConfig} onClose={() => setDietSetup(false)} initialType={activeDietType as DietType} initialWeeks={activePlanWeeks as 1|2|3|4} />}

        {/* Add / Edit food modal */}
        {foodModal && (() => {
          const section = weekSections.find(s => s.id === foodModal.sectionId);
          const sectionType = section?.type ?? "";
          const isEdit = foodModal.editId !== undefined;

          // Filter library: by category matching section type, then by search
          const catFoods = foodLibrary.filter(f =>
            !sectionType || (f.category ?? "Other").toLowerCase() === sectionType.toLowerCase()
          );
          const visibleFoods = (librarySearch.trim()
            ? foodLibrary.filter(f => f.name.toLowerCase().includes(librarySearch.toLowerCase()))
            : catFoods
          );

          return (
            <div className={classes.modal_overlay} onClick={() => setFoodModal(null)}>
              <div className={classes.food_modal} onClick={e => e.stopPropagation()}>

                {/* Header */}
                <div className={classes.modal_header}>
                  <h3 className={classes.modal_title}>
                    {isEdit ? "Edit Food" : `Add Food${sectionType ? ` — ${sectionType}` : ""}`}
                  </h3>
                  <button className={classes.modal_close} onClick={() => setFoodModal(null)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><path d="M18 6 6 18M6 6l12 12"/></svg>
                  </button>
                </div>

                <div className={classes.food_accordion}>

                  {/* ── Section 1: Browse predefined foods ── */}
                  {!isEdit && (
                    <div className={classes.acc_section}>
                      <button className={`${classes.acc_header} ${browseOpen ? classes.acc_header_open : ""}`} onClick={() => setBrowseOpen(o => !o)}>
                        <div className={classes.acc_header_left}>
                          <div className={classes.acc_icon}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                          </div>
                          <div>
                            <div className={classes.acc_title}>Browse Food Library</div>
                            <div className={classes.acc_sub}>{catFoods.length} {sectionType || "food"}{catFoods.length !== 1 ? "s" : ""} available</div>
                          </div>
                        </div>
                        <svg className={`${classes.acc_chevron} ${browseOpen ? classes.acc_chevron_open : ""}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>
                      </button>

                      <div className={`${classes.acc_body} ${browseOpen ? classes.acc_body_open : ""}`}>
                        <div className={classes.acc_body_inner}>
                          <div className={classes.food_browser_search}>
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/></svg>
                            <input
                              className={classes.food_browser_input}
                              placeholder={libraryLoading ? "Loading…" : `Search${sectionType ? ` ${sectionType} foods` : ""}…`}
                              value={librarySearch}
                              onChange={e => setLibrarySearch(e.target.value)}
                              disabled={libraryLoading}
                            />
                            {librarySearch && <button className={classes.food_browser_clear} onClick={() => setLibrarySearch("")}>✕</button>}
                          </div>

                          <div className={classes.food_list}>
                            {libraryLoading && <div className={classes.food_list_empty}>Loading library…</div>}
                            {!libraryLoading && visibleFoods.length === 0 && (
                              <div className={classes.food_list_empty}>No foods found</div>
                            )}
                            {!libraryLoading && visibleFoods.map(f => {
                              const qty = f.quantity ? `${f.quantity}${f.unit ? ` ${f.unit}` : ""}` : "";
                              const selected = foodFromLibrary && foodForm.name === f.name;
                              return (
                                <button
                                  key={f.id}
                                  className={`${classes.food_card} ${selected ? classes.food_card_selected : ""}`}
                                  onClick={() => {
                                    const baseQty = parseFloat(f.quantity) || 1;
                                    setBaseMacros({ qty: baseQty, cal: f.cal, protein: f.protein, fat: f.fat, carb: f.carb });
                                    setFoodForm({ name: f.name, quantity: String(baseQty), unit: f.unit ?? "", cal: String(f.cal), protein: String(f.protein), fat: String(f.fat), carb: String(f.carb) });
                                    setFoodFromLibrary(true);
                                    setFoodError("");
                                    setManualOpen(true);
                                  }}
                                >
                                  <div className={classes.food_card_name}>{f.name}</div>
                                  {qty && <div className={classes.food_card_qty}>{qty}</div>}
                                  <div className={classes.food_card_macros}>
                                    <span className={classes.macro_cal}>{f.cal} kcal</span>
                                    <span className={classes.macro_chip}>P {f.protein}g</span>
                                    <span className={classes.macro_chip}>F {f.fat}g</span>
                                    <span className={classes.macro_chip}>C {f.carb}g</span>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* ── Section 2: Manual entry ── */}
                  <div className={classes.acc_section}>
                    <button className={`${classes.acc_header} ${manualOpen || isEdit ? classes.acc_header_open : ""}`} onClick={() => !isEdit && setManualOpen(o => !o)}>
                      <div className={classes.acc_header_left}>
                        <div className={classes.acc_icon}>
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </div>
                        <div>
                          <div className={classes.acc_title}>{isEdit ? "Edit Details" : "Enter Manually"}</div>
                          <div className={classes.acc_sub}>{foodFromLibrary && !isEdit ? `Selected: ${foodForm.name}` : "Fill in food details"}</div>
                        </div>
                      </div>
                      {!isEdit && <svg className={`${classes.acc_chevron} ${manualOpen ? classes.acc_chevron_open : ""}`} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><polyline points="6 9 12 15 18 9"/></svg>}
                    </button>

                    <div className={`${classes.acc_body} ${manualOpen || isEdit ? classes.acc_body_open : ""}`}>
                      <div className={classes.acc_body_inner}>
                        <div className={classes.form_grid}>
                          <div className={`${classes.field} ${classes.field_full}`}>
                            <label className={classes.field_label}>Food Name</label>
                            <input className={classes.field_input} placeholder="e.g. Grilled Chicken Breast"
                              value={foodForm.name} onChange={e => ff("name", e.target.value)}
                              readOnly={foodFromLibrary || isEdit} />
                          </div>
                          <div className={`${classes.field} ${classes.field_full}`}>
                            <label className={classes.field_label}>Quantity</label>
                            {foodFromLibrary && !isEdit ? (
                              <div className={classes.qty_row}>
                                <input className={classes.field_input} type="number" min="0" placeholder="0"
                                  value={foodForm.quantity} onChange={e => handleQtyChange(e.target.value)} />
                                {foodForm.unit && <span className={classes.qty_unit}>{foodForm.unit}</span>}
                              </div>
                            ) : (
                              <div className={classes.qty_row}>
                                <input className={classes.field_input} type="number" min="0" placeholder="0"
                                  value={foodForm.quantity} onChange={e => handleQtyChange(e.target.value)} />
                                <select className={classes.unit_select} value={foodForm.unit} onChange={e => ff("unit", e.target.value)}>
                                  <option value="g">g</option>
                                  <option value="mg">mg</option>
                                  <option value="kg">kg</option>
                                  <option value="ml">ml</option>
                                  <option value="L">L</option>
                                  <option value="cups">cups</option>
                                  <option value="tbsp">tbsp</option>
                                  <option value="tsp">tsp</option>
                                  <option value="oz">oz</option>
                                  <option value="lb">lb</option>
                                  <option value="piece">piece</option>
                                  <option value="serving">serving</option>
                                </select>
                              </div>
                            )}
                          </div>
                          <div className={classes.field}>
                            <label className={classes.field_label}>Calories (kcal)</label>
                            <input className={classes.field_input} type="number" placeholder="0"
                              value={foodForm.cal} onChange={e => ff("cal", e.target.value)}
                              readOnly={foodFromLibrary || isEdit} />
                          </div>
                          <div className={classes.field}>
                            <label className={classes.field_label}>Protein (g)</label>
                            <input className={classes.field_input} type="number" placeholder="0"
                              value={foodForm.protein} onChange={e => ff("protein", e.target.value)}
                              readOnly={foodFromLibrary || isEdit} />
                          </div>
                          <div className={classes.field}>
                            <label className={classes.field_label}>Fat (g)</label>
                            <input className={classes.field_input} type="number" placeholder="0"
                              value={foodForm.fat} onChange={e => ff("fat", e.target.value)}
                              readOnly={foodFromLibrary || isEdit} />
                          </div>
                          <div className={classes.field}>
                            <label className={classes.field_label}>Carbs (g)</label>
                            <input className={classes.field_input} type="number" placeholder="0"
                              value={foodForm.carb} onChange={e => ff("carb", e.target.value)}
                              readOnly={foodFromLibrary || isEdit} />
                          </div>
                        </div>
                        {foodError && <p className={classes.food_form_error}>{foodError}</p>}
                      </div>
                    </div>
                  </div>

                </div>

                {/* Footer actions */}
                <div className={classes.modal_actions}>
                  <button className={classes.cancel_btn} onClick={() => setFoodModal(null)} disabled={foodSaving}>Cancel</button>
                  <button className={classes.save_btn} onClick={saveFood} disabled={foodSaving || !foodForm.name.trim()}>
                    {foodSaving ? "Saving…" : isEdit ? "Save Changes" : "Add Food"}
                  </button>
                </div>

              </div>
            </div>
          );
        })()}

        {/* ── Diet History ─────────────────────────────── */}
        {renderDietHistory()}

        {/* ── Food kebab menu ───────────────────────────── */}
        {foodMenu && foodMenuAnchor && (() => {
          const sec = (dietPlan[selWeek]?.sections ?? []).find(s => s.id === foodMenu.sectionId);
          const opt = sec?.items.find(o => o.id === foodMenu.optId);
          if (!opt) return null;
          return (
            <div
              className={classes.kebab_menu}
              style={{ position: "fixed", top: foodMenuAnchor.top, right: foodMenuAnchor.right, zIndex: 300 }}
              onClick={e => e.stopPropagation()}
            >
              <button className={classes.kebab_item} onClick={() => { openEditFood(foodMenu.sectionId, opt); setFoodMenu(null); }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                Edit quantity
              </button>
              <button className={`${classes.kebab_item} ${classes.kebab_danger}`} onClick={() => { deleteFood(foodMenu.sectionId, foodMenu.optId); setFoodMenu(null); }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/></svg>
                Delete
              </button>
            </div>
          );
        })()}
      </div>
    );
  };

  const renderMeasurements = () => (
    <div className={classes.meas_section}>
      <div className={classes.meas_header}>
        <h3 className={classes.section_title}>Measurement Records</h3>
        <button className={classes.add_meas_btn} onClick={() => setMeasEdit(true)}>
          + Add Record
        </button>
      </div>

      {measurements.length === 0 && <p className={classes.empty_msg}>No records yet. Add one above.</p>}
      {measurements.map((m: MeasurementRecord, i: number) => (
        <div key={i} className={classes.meas_card}>
          <div className={classes.meas_card_header}>
            <div className={classes.meas_date}>{m.date}</div>
            <div className={classes.meas_menu_wrap}>
              <button
                className={classes.meas_menu_btn}
                onClick={() => setMeasMenuOpen(prev => prev === m.id ? null : (m.id ?? String(i)))}
              >
                <span /><span /><span />
              </button>
              {measMenuOpen === (m.id ?? String(i)) && (
                <>
                  <div className={classes.meas_menu_backdrop} onClick={() => setMeasMenuOpen(null)} />
                  <div className={classes.meas_menu_dropdown}>
                    <button className={classes.meas_menu_item} onClick={() => { setMeasMenuOpen(null); openEditMeas(m); }}>
                      Edit
                    </button>
                    <button className={`${classes.meas_menu_item} ${classes.meas_menu_item_red}`} onClick={() => deleteMeasurement(m)}>
                      Delete
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
          <div className={classes.meas_grid}>
            {([
              ["Weight",       m.weight      != null ? `${m.weight} kg`      : null],
              ["Height",       m.height      != null ? `${m.height} cm`      : null],
              ["Body Fat %",   m.bodyFatPct  != null ? `${m.bodyFatPct}%`    : null],
              ["Muscle",       m.musclePct   != null ? `${m.musclePct}%`     : null],
              ["Water %",      m.waterPct    != null ? `${m.waterPct}%`      : null],
              ["Ideal Weight", m.idealWeight != null ? `${m.idealWeight} kg` : null],
              ["Waist",        m.waist       != null ? `${m.waist} cm`       : null],
              ["Arm",          m.arm         != null ? `${m.arm} cm`         : null],
              ["Wrist",        m.wrist       != null ? `${m.wrist} cm`       : null],
              ["Hips",         m.hips        != null ? `${m.hips} cm`        : null],
              ["Fat Clipper",  m.fatClipper  != null ? `${m.fatClipper} mm`  : null],
              ["Visceral Fat", m.visceralFat != null ? String(m.visceralFat) : null],
            ] as [string, string | null][]).filter(([, v]) => v !== null).map(([k, v]) => (
              <div key={k} className={classes.meas_item}>
                <span className={classes.meas_label}>{k}</span>
                <span className={classes.meas_value}>{v}</span>
              </div>
            ))}
          </div>
        </div>
      ))}

      {/* Add measurement modal */}
      {measEdit && (
        <div className={classes.modal_overlay} onClick={() => { setMeasEdit(false); setEditingMeas(null); setMeasForm(EMPTY_MEAS); }}>
          <div className={classes.modal_box} onClick={e => e.stopPropagation()}>
            <div className={classes.modal_header}>
              <h3 className={classes.modal_title}>{editingMeas ? "Edit Measurement" : "Add Measurement Record"}</h3>
              <button className={classes.modal_close} onClick={() => { setMeasEdit(false); setEditingMeas(null); setMeasForm(EMPTY_MEAS); }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M18 6 6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>
            <div className={classes.form_grid}>
              <div className={classes.field} style={{ gridColumn: "1 / -1" }}>
                <label className={classes.field_label}>Date</label>
                <input
                  className={classes.field_input}
                  type="date"
                  value={measForm.date}
                  onChange={e => setMeasForm(prev => ({ ...prev, date: e.target.value }))}
                />
              </div>
              {([
                { key: "weight",      label: "Weight (kg)" },
                { key: "waist",       label: "Waist (cm)" },
                { key: "arm",         label: "Arm (cm)" },
                { key: "wrist",       label: "Wrist (cm)" },
                { key: "hips",        label: "Hips (cm)" },
                { key: "fatClipper",  label: "Fat Clipper (mm)" },
                { key: "visceralFat", label: "Visceral Fat" },
                { key: "bodyFatPct",  label: "Body Fat %" },
                { key: "musclePct",   label: "Muscle %" },
                { key: "waterPct",    label: "Water %" },
              ] as { key: keyof typeof measForm; label: string }[]).map(({ key, label }) => (
                <div key={key} className={classes.field}>
                  <label className={classes.field_label}>{label}</label>
                  <input
                    className={classes.field_input}
                    type="number"
                    placeholder="0"
                    value={measForm[key]}
                    onChange={e => setMeasForm(prev => ({ ...prev, [key]: e.target.value }))}
                  />
                </div>
              ))}
            </div>
            <div className={classes.modal_actions}>
              <button className={classes.cancel_btn} onClick={() => { setMeasEdit(false); setEditingMeas(null); setMeasForm(EMPTY_MEAS); }} disabled={saving}>Cancel</button>
              <button className={classes.save_btn} onClick={saveMeasurement} disabled={saving || requiredMeasFields.some(f => !measForm[f])}>
                {saving ? <span className={classes.btn_spinner} /> : (editingMeas ? "Save Changes" : "Save Record")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const renderProgressChart = (
    label: string,
    pts: { date: string; val: number }[],
    color: string,
    unit: string,
    gradId: string,
  ) => {
    if (pts.length < 2) return <p className={classes.empty_msg}>Not enough data yet — add at least 2 records with {label.toLowerCase()}.</p>;
    const max   = Math.max(...pts.map(p => p.val));
    const min   = Math.min(...pts.map(p => p.val));
    const range = max - min || 1;
    const W = 520, H = 180, pad = 30;
    const xStep = (W - pad * 2) / (pts.length - 1);
    const y = (v: number) => pad + (1 - (v - min) / range) * (H - pad * 2);
    const x = (i: number) => pad + i * xStep;
    const pathD = pts.map((p, i) => `${i === 0 ? "M" : "L"} ${x(i)} ${y(p.val)}`).join(" ");
    const areaD = `${pathD} L ${x(pts.length - 1)} ${H - pad} L ${x(0)} ${H - pad} Z`;
    return (
      <div className={classes.chart_card}>
        <h3 className={classes.card_title}>{label} Progress</h3>
        <svg viewBox={`0 0 ${W} ${H}`} className={classes.chart_svg}>
          <defs>
            <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%"   stopColor={color} stopOpacity="0.25"/>
              <stop offset="100%" stopColor={color} stopOpacity="0"/>
            </linearGradient>
          </defs>
          <path d={areaD} fill={`url(#${gradId})`} />
          <path d={pathD} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"/>
          {pts.map((p, i) => (
            <g key={i}>
              <circle cx={x(i)} cy={y(p.val)} r="4" fill={color} />
              <text x={x(i)} y={y(p.val) - 10} textAnchor="middle" fontSize="10" fill={color} fontWeight="700">{p.val}{unit}</text>
              <text x={x(i)} y={H - 8} textAnchor="middle" fontSize="10" fill="#7A8C7A">{p.date}</text>
            </g>
          ))}
        </svg>
      </div>
    );
  };

  const renderProgress = () => {
    // Build data points from measurement records (sorted oldest → newest)
    const sorted = [...measurements].reverse();

    const weightPts  = sorted.filter(m => m.weight      != null).map(m => ({ date: m.date, val: m.weight!      }));
    const bodyFatPts = sorted.filter(m => m.bodyFatPct  != null).map(m => ({ date: m.date, val: m.bodyFatPct! }));
    const musclePts  = sorted.filter(m => m.musclePct   != null).map(m => ({ date: m.date, val: m.musclePct!   }));
    const waterPts   = sorted.filter(m => m.waterPct    != null).map(m => ({ date: m.date, val: m.waterPct!    }));
    const fatPts     = sorted.filter(m => m.fatClipper  != null).map(m => ({ date: m.date, val: m.fatClipper!  }));

    const hasAny = weightPts.length || bodyFatPts.length || musclePts.length || waterPts.length || fatPts.length;
    if (!hasAny) return <p className={classes.empty_msg}>No data yet — add measurement records to see progress.</p>;

    return (
      <div className={classes.progress_section}>
        {weightPts.length  >= 2 && renderProgressChart("Weight",       weightPts,  "#4A7C40", " kg", "wg")}
        {bodyFatPts.length >= 2 && renderProgressChart("Body Fat",     bodyFatPts, "#E05C5C", "%",   "bf")}
        {musclePts.length  >= 2 && renderProgressChart("Muscle",       musclePts,  "#3B7CC9", "%",   "mg")}
        {waterPts.length   >= 2 && renderProgressChart("Water",        waterPts,   "#2196B0", "%",   "wtr")}
        {fatPts.length     >= 2 && renderProgressChart("Fat Clipper",  fatPts,     "#C97A3B", " mm", "fg")}
        {(weightPts.length < 2 && bodyFatPts.length < 2 && musclePts.length < 2 && waterPts.length < 2 && fatPts.length < 2) &&
          <p className={classes.empty_msg}>Add at least 2 measurement records to see progress charts.</p>}
      </div>
    );
  };

  return (
    <>
      {/* Topbar */}
      <header className={classes.topbar}>
        <div className={classes.topbar_left}>
          <div className={`${classes.topbar_avatar} ${localClient.status === "pending" ? classes.topbar_avatar_pending : ""}`}>
            {localClient.name.split(" ").map(w => w[0]).join("").slice(0, 2).toUpperCase()}
          </div>
          <div className={classes.topbar_text}>
            <h1 className={classes.page_title}>{localClient.name}</h1>
            <button className={classes.back_btn} onClick={() => navigate("/dietitian/clients")}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                <path d="M19 12H5M12 5l-7 7 7 7"/>
              </svg>
              Back to Clients
            </button>
          </div>
        </div>
        <div className={classes.topbar_right}>
          <span className={`${classes.status_pill} ${localClient.status === "active" ? classes.status_active : localClient.status === "pending" ? classes.status_pending : classes.status_inactive}`}>
            {localClient.status}
          </span>
        </div>
      </header>

      {/* Pending banner */}
      {localClient.status === "pending" && (
        <div className={classes.pending_banner}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/></svg>
          This client registered online. Their profile is incomplete — fill in health data at the first appointment.
          <button className={classes.complete_profile_link} onClick={openProfileEdit}>Complete Profile →</button>
        </div>
      )}

      {/* Tab bar */}
      <div className={classes.tab_bar}>
        {(["overview","diet","measurements","progress"] as Tab[]).map(t => (
          <button
            key={t}
            className={`${classes.tab} ${tab === t ? classes.tab_active : ""}`}
            onClick={() => setTab(t)}
          >
            {t.charAt(0).toUpperCase() + t.slice(1)}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className={classes.content}>
        {tab === "overview"      && renderOverview()}
        {tab === "diet"          && renderDiet()}
        {tab === "measurements"  && renderMeasurements()}
        {tab === "progress"      && renderProgress()}
      </div>

      {/* Edit / Complete Profile Modal */}
      {profileEdit && (
        <div className={classes.modal_overlay} onClick={() => setProfileEdit(false)}>
          <div className={classes.modal_box} onClick={e => e.stopPropagation()}>
            <div className={classes.modal_header}>
              <h3 className={classes.modal_title}>
                {localClient.status === "pending" ? "Complete Profile" : "Edit Profile"}
              </h3>
              <button className={classes.modal_close} onClick={() => setProfileEdit(false)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <path d="M18 6 6 18M6 6l12 12"/>
                </svg>
              </button>
            </div>
            <div className={classes.form_grid}>
              <div className={`${classes.field} ${classes.field_full}`}>
                <label className={classes.field_label}>Full Name</label>
                <input className={classes.field_input} value={profileForm.name}
                  onChange={e => setProfileForm(p => ({ ...p, name: e.target.value }))} />
              </div>
              <div className={`${classes.field} ${classes.field_full}`}>
                <label className={classes.field_label}>Email</label>
                <input className={classes.field_input} type="email" value={profileForm.email}
                  onChange={e => setProfileForm(p => ({ ...p, email: e.target.value }))} />
              </div>
              <div className={classes.field}>
                <label className={classes.field_label}>Age</label>
                <input className={classes.field_input} type="number" placeholder="e.g. 28" value={profileForm.age}
                  onChange={e => setProfileForm(p => ({ ...p, age: e.target.value }))} />
              </div>
              <div className={classes.field}>
                <label className={classes.field_label}>Gender</label>
                <select className={classes.field_input} value={profileForm.gender}
                  onChange={e => setProfileForm(p => ({ ...p, gender: e.target.value as "M" | "F" }))}>
                  <option value="F">Female</option>
                  <option value="M">Male</option>
                </select>
              </div>
              <div className={classes.field}>
                <label className={classes.field_label}>Phone</label>
                <input className={classes.field_input} placeholder="+961 70 XXX XXX" value={profileForm.phone}
                  onChange={e => setProfileForm(p => ({ ...p, phone: e.target.value }))} />
              </div>
              <div className={classes.field}>
                <label className={classes.field_label}>Height (cm) <span style={{ color: "#C0392B" }}>*</span></label>
                <input className={classes.field_input} type="number" placeholder="e.g. 175" value={profileForm.height}
                  onChange={e => setProfileForm(p => ({ ...p, height: e.target.value }))} />
              </div>

              <div className={`${classes.field} ${classes.field_full}`}>
                <label className={classes.field_label}>Goal</label>
                <input className={classes.field_input} placeholder="e.g. Weight Loss, Muscle Gain…" value={profileForm.goal}
                  onChange={e => setProfileForm(p => ({ ...p, goal: e.target.value }))} />
              </div>
              <div className={classes.field}>
                <label className={classes.field_label}>Start Weight (kg)</label>
                <input className={classes.field_input} type="number" placeholder="0" value={profileForm.startWeight}
                  onChange={e => setProfileForm(p => ({ ...p, startWeight: e.target.value }))} />
              </div>
              <div className={classes.field}>
                <label className={classes.field_label}>Current Weight (kg)</label>
                <input className={classes.field_input} type="number" placeholder="0" value={profileForm.currentWeight}
                  onChange={e => setProfileForm(p => ({ ...p, currentWeight: e.target.value }))} />
              </div>
              <div className={classes.field}>
                <label className={classes.field_label}>Ideal Weight (kg)</label>
                <input className={classes.field_input} type="number" readOnly
                  value={idealWeightCalc ?? ""} placeholder="Fill height, age & gender"
                  style={{ background: "#f7f7f7", color: "#666", cursor: "default" }} />
              </div>
              <div className={classes.field}>
                <label className={classes.field_label}>Body Fat %</label>
                <input className={classes.field_input} type="number" placeholder="0" value={profileForm.bodyFatPct}
                  onChange={e => setProfileForm(p => ({ ...p, bodyFatPct: e.target.value }))} />
              </div>
              <div className={classes.field}>
                <label className={classes.field_label}>Muscle % <span style={{ color: "#C0392B" }}>*</span></label>
                <input className={classes.field_input} type="number" placeholder="0" value={profileForm.musclePct}
                  onChange={e => setProfileForm(p => ({ ...p, musclePct: e.target.value }))} />
              </div>
              <div className={classes.field}>
                <label className={classes.field_label}>Water % <span style={{ color: "#C0392B" }}>*</span></label>
                <input className={classes.field_input} type="number" placeholder="0" value={profileForm.waterPct}
                  onChange={e => setProfileForm(p => ({ ...p, waterPct: e.target.value }))} />
              </div>
            </div>
            {profileError && (
              <p style={{ color: "#C0392B", fontSize: 12, margin: "0 0 8px", padding: "0 4px" }}>{profileError}</p>
            )}
            <div className={classes.modal_actions}>
              <button className={classes.cancel_btn} onClick={() => { setProfileEdit(false); setProfileError(""); }} disabled={saving}>Cancel</button>
              <button className={classes.save_btn} onClick={saveProfile} disabled={saving}>
                {saving ? <span className={classes.btn_spinner} /> : "Save Profile"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Complete diet confirmation modal ────────────── */}
      {confirmComplete && (
        <div className={classes.modal_overlay} onClick={() => setConfirmComplete(false)}>
          <div className={classes.confirm_modal} onClick={e => e.stopPropagation()}>
            <div className={classes.confirm_icon}>
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="20 6 9 17 4 12"/>
              </svg>
            </div>
            <h3 className={classes.confirm_title}>Complete Diet Plan?</h3>
            <p className={classes.confirm_body}>
              This will archive the current plan to history and reset the diet configuration. This cannot be undone.
            </p>
            <div className={classes.confirm_actions}>
              <button className={classes.cancel_btn} onClick={() => setConfirmComplete(false)}>Cancel</button>
              <button className={classes.confirm_ok_btn} onClick={doCompleteDiet}>Mark as Complete</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ClientDetail;
